import { useState } from 'react';
import { COMPASS_WARNING, INSTALL, PROPERTIES, PLACEHOLDER_BOUNDS, PLACEHOLDER_NOTICE } from '@kilroy/shared';
import { setMeta } from '../db/db';
import { useSession } from '../app/session';
import { IconCamera, IconCheck, IconFix, IconMic } from '../components/icons';
import './onboarding.css';

/* Onboarding is data safety, not decoration.
 *
 * Four screens, each of which exists because skipping it costs someone their
 * work: install (storage eviction), sign-in (offline token), permissions
 * (asked in context later, primed here), offline pack (no signal out there).
 * Nothing here is a feature tour.
 */

export function Onboarding({ onDone }: { onDone: () => void }) {
  const { standalone, user } = useSession();
  const [step, setStep] = useState(0);

  const finish = async () => {
    await setMeta('onboarded', true);
    onDone();
  };

  const steps = [
    {
      key: 'welcome',
      title: 'Kilroy Field',
      body: (
        <>
          <p className="ob__lead">
            Everything you capture saves to this phone the moment you tap Save — signal or
            no signal. The network is something that happens later.
          </p>
          <p className="ob__lead">
            Nothing is ever edited or deleted. Corrections are added on top, and the
            original stays underneath. That is what makes this record worth anything to a
            QP.
          </p>
        </>
      ),
      cta: 'Start',
    },
    {
      key: 'install',
      title: standalone ? 'Installed' : 'Add to home screen',
      body: standalone ? (
        <div className="ob__done">
          <IconCheck size={30} />
          <p className="ob__lead">{INSTALL.done}</p>
        </div>
      ) : (
        <>
          <p className="ob__lead">{INSTALL.why}</p>
          <ol className="steps">
            <li>
              <span className="steps__n mono">1</span>
              <span>Tap <strong>Share</strong> in Safari’s toolbar.</span>
            </li>
            <li>
              <span className="steps__n mono">2</span>
              <span>Choose <strong>Add to Home Screen</strong>.</span>
            </li>
            <li>
              <span className="steps__n mono">3</span>
              <span>Open Kilroy from the icon from now on.</span>
            </li>
          </ol>
          <p className="field__hint">
            You can carry on without this — but the banner stays up until it’s done.
          </p>
        </>
      ),
      cta: standalone ? 'Next' : 'I’ll do it now',
    },
    {
      key: 'permissions',
      title: 'Three permissions',
      body: (
        <>
          <p className="ob__lead">
            Each is asked the first time you need it, not now. Here’s what they’re for.
          </p>
          <ul className="perms">
            <li>
              <IconFix size={24} />
              <div>
                <strong>Location</strong>
                <p>Where the observation is. Accuracy is recorded with it, always.</p>
              </div>
            </li>
            <li>
              <IconCamera size={24} />
              <div>
                <strong>Camera</strong>
                <p>Original photos, kept exactly as the camera made them.</p>
              </div>
            </li>
            <li>
              <IconMic size={24} />
              <div>
                <strong>Microphone</strong>
                <p>Spoken notes. Transcribed on sync; the audio is never deleted.</p>
              </div>
            </li>
          </ul>
          <p className="warning">{COMPASS_WARNING}</p>
        </>
      ),
      cta: 'Next',
    },
    {
      key: 'packs',
      title: 'Offline maps',
      body: (
        <>
          <p className="ob__lead">
            Download a property before you lose signal. Each pack carries topography,
            contours and claim boundaries for one property.
          </p>
          <ul className="packs">
            {PROPERTIES.map((p) => (
              <li key={p.id} className="pack">
                <div className="grow">
                  <strong>{p.name}</strong>
                  <div className="mono pack__meta">≈{p.pack_mb} MB</div>
                </div>
                <button type="button" className="btn btn--sand" disabled={PLACEHOLDER_BOUNDS}>
                  Download
                </button>
              </li>
            ))}
          </ul>
          {PLACEHOLDER_BOUNDS && <p className="notice">{PLACEHOLDER_NOTICE}</p>}
          <p className="field__hint">
            You can do this later from Settings. The map still works without a pack — it
            just won’t have terrain.
          </p>
        </>
      ),
      cta: 'Done',
    },
  ];

  const current = steps[step];
  const last = step === steps.length - 1;

  return (
    <div className="ob">
      <div className="ob__body scroll">
        <div className="ob__progress mono" aria-hidden="true">
          {steps.map((s, i) => (
            <span key={s.key} className={`ob__tick${i <= step ? ' ob__tick--on' : ''}`} />
          ))}
        </div>

        <h1 className="ob__title">{current.title}</h1>
        <div className="ob__content">{current.body}</div>
      </div>

      <div className="ob__foot">
        <button
          type="button"
          className="btn btn--primary btn--lg"
          onClick={() => (last ? void finish() : setStep(step + 1))}
        >
          {current.cta}
        </button>
        {!last && (
          <button type="button" className="ob__skip mono" onClick={() => void finish()}>
            Skip setup
          </button>
        )}
        {step === 0 && <p className="ob__who mono">Signed in as {user.name}</p>}
      </div>
    </div>
  );
}
